/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MPC_PRODUCT_LEGEND_ID,
  MPC_PRODUCT_LEGEND_SWATCH_COLOR
} from "constants/chart.constants";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import { calculateTextDimensions } from "utils/dom";

import { AXIS_NAME_TEXT_STYLE } from "components/multiphase-chart/constants/shared.constants";

import { getProductOverrideColor } from "./getEffectiveSeriesColor";
import {
  SWATCH_REFERENCE_WIDTH,
  SWATCH_WIDTH,
  buildProductStyleMap,
  resolveProductLegendLabel
} from "./mpcTranslatorHelpers";

export interface ProductLegendItem {
  product: string;
  label: string;
  lineStyleType: string | number[];
  /**
   * Per-product swatch colour when "Override bin colours" is active; undefined
   * otherwise (the swatch then uses the neutral MPC_PRODUCT_LEGEND_SWATCH_COLOR,
   * since without the override colour encodes bin, not product).
   */
  color?: string;
}

// Layout constants for the horizontal product legend (one row of swatch + label).
const DASH_SCALE = SWATCH_WIDTH / SWATCH_REFERENCE_WIDTH;
const SWATCH_LINE_WIDTH = 2; // px stroke thickness of the swatch
const SWATCH_TEXT_GAP = 6; // px between swatch and its label
const ITEM_GAP = 22; // px between one item and the next
// Match the x-axis title style so the bottom legend reads consistently with
// the axis titles on these charts.
const FONT_SIZE = AXIS_NAME_TEXT_STYLE.fontSize;
const TEXT_COLOR = AXIS_NAME_TEXT_STYLE.color;

/**
 * One legend item per unique product, carrying the same line style the chart
 * draws for that product (via buildProductStyleMap). Pure — no ECharts shape.
 */
export function buildProductLegendData(multiProduct: any): ProductLegendItem[] {
  const styleMap = buildProductStyleMap(multiProduct);
  // When "Override bin colours" is on the chart lines are coloured per product,
  // so the legend swatches match that colour; otherwise they stay neutral.
  const overrideBinColors = !!multiProduct?.properties?.overrideBinColors;
  const items: ProductLegendItem[] = [];
  styleMap.forEach((lineStyleType, product) => {
    items.push({
      product,
      label: resolveProductLegendLabel(product),
      lineStyleType,
      color: overrideBinColors ? getProductOverrideColor(product) : undefined
    });
  });
  return items;
}

/**
 * Builds an ECharts `graphic` group rendering the product legend as a single
 * centred row of line-style swatches + labels, or null when there are no
 * products. We use `graphic` rather than a native `legend` because this
 * echarts build only draws legend items that correspond to a series, and the
 * product legend is intentionally decoupled from the per-bin series.
 */
export function buildProductLegendGraphic(items: ProductLegendItem[]): any | null {
  if (!items.length) return null;

  const children: any[] = [];
  let x = 0;
  for (const it of items) {
    const lineDash =
      it.lineStyleType === "solid"
        ? undefined
        : (it.lineStyleType as number[]).map((seg) => seg * DASH_SCALE);
    // Line swatch, vertically centred on y = 0. Uses the product's override
    // colour when present, else the neutral swatch colour.
    children.push({
      type: "line",
      silent: true,
      shape: { x1: x, y1: 0, x2: x + SWATCH_WIDTH, y2: 0 },
      style: {
        stroke: it.color ?? MPC_PRODUCT_LEGEND_SWATCH_COLOR,
        lineWidth: SWATCH_LINE_WIDTH,
        lineDash
      }
    });
    const textX = x + SWATCH_WIDTH + SWATCH_TEXT_GAP;
    children.push({
      type: "text",
      silent: true,
      x: textX,
      y: 0,
      style: {
        text: it.label,
        fill: TEXT_COLOR,
        fontSize: FONT_SIZE,
        fontFamily: THEME_FONT_FAMILY,
        align: "left",
        verticalAlign: "middle"
      }
    });
    // Measure the label in the same font it renders in so the gap before the
    // next item is uniform (a char-count estimate makes spacing look uneven).
    const labelWidth = calculateTextDimensions(
      it.label,
      FONT_SIZE,
      THEME_FONT_FAMILY
    ).width;
    x = textX + labelWidth + ITEM_GAP;
  }

  return {
    id: MPC_PRODUCT_LEGEND_ID,
    type: "group",
    left: "center",
    bottom: 4,
    children
  };
}

/**
 * Mutates and returns the chart options: when MPC is active and the product
 * legend is toggled on, attaches the product-legend graphic. No-op otherwise
 * (so existing translator snapshots are unaffected). Bottom-grid spacing for
 * the legend is reserved downstream in applySpacingToChartOptions, keyed off
 * the graphic's id. Callers pass a freshly built options object (translators
 * construct options per call), so in-place mutation is safe.
 */
export function applyProductLegend(options: any, multiProduct: any): any {
  if (!options || !multiProduct?.active || !multiProduct?.properties?.showProductLegend) {
    return options;
  }
  const graphic = buildProductLegendGraphic(buildProductLegendData(multiProduct));
  if (!graphic) return options;

  // Append rather than overwrite — the base chart options seed `graphic: []`
  // and other features may contribute their own graphic elements.
  options.graphic = [...(options.graphic ?? []), graphic];
  return options;
}
